/**
 * Signing in on Birbly's own screens: Cognito's API, called directly.
 *
 * Why (2026-10-19): in the app, the hosted sign-in page opened in the
 * system browser, and a parent signing in watched Birbly vanish behind a
 * Chrome or Safari sheet. Email-and-password does not need a browser at all
 * -- Cognito's user-pool API takes an SRP proof (auth/srp.ts) from any
 * client -- so it now happens here, on the app's own screens, on the website
 * too. Only Google and Apple still need a browser sheet, because Google
 * refuses sign-in inside an app's own web view.
 *
 * Same pool, same app client, same tokens as the hosted page: the API's JWT
 * authorizer checks the access token's issuer and client_id, which are the
 * same whichever way the parent signed in. The password never leaves the
 * device -- the app client does not even allow USER_PASSWORD_AUTH.
 *
 * Talks to exactly one place, https://cognito-idp.<region>.amazonaws.com,
 * which config.ts already allows and both Content-Security-Policies already
 * list.
 */

import { assertKnownOrigin, config } from '../config';
import { ephemeral, passwordVerifier } from './srp';

export interface Tokens {
  AccessToken: string;
  IdToken: string;
  RefreshToken?: string;
  ExpiresIn: number;
}

/** A code Cognito wants before it hands over tokens (an authenticator app, a text, an email). */
export type CodeChallenge = 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA' | 'EMAIL_OTP';

export type SignInStep =
  | { kind: 'done'; tokens: Tokens }
  | { kind: 'code'; challenge: CodeChallenge; session: string; username: string; destination?: string }
  | { kind: 'new-password'; session: string; username: string };

export class CognitoError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'CognitoError';
  }
}

/** What a person should read, by Cognito's error type. */
export function friendly(code: string, message: string): string {
  switch (code) {
    case 'NotAuthorizedException':
      return /disabled/i.test(message)
        ? 'This account has been turned off. Write to us if that is a surprise.'
        : /refresh token/i.test(message)
          ? 'Your sign-in has expired. Please sign in again.'
          : 'That email and password do not match.';
    case 'UserNotConfirmedException':
      return 'Confirm your email first: we sent a code when you signed up.';
    case 'UsernameExistsException':
      return 'There is already an account for that email. Sign in instead.';
    case 'InvalidPasswordException':
      return 'Use a password of at least 12 characters.';
    case 'CodeMismatchException':
      return 'That code is not right. Check the email and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Ask for a new one.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many tries. Wait a few minutes, then try again.';
    case 'UserLambdaValidationException':
      // The pre-sign-up trigger's own words (an email already used with Google).
      return message.replace(/^PreSignUp failed with error\s*/i, '').replace(/\.?$/, '.');
    case 'InvalidParameterException':
      return /email/i.test(message) ? 'That does not look like an email address.' : message;
    default:
      return message || 'Something went wrong. Please try again.';
  }
}

async function call<T>(operation: string, body: Record<string, unknown>): Promise<T> {
  const endpoint = `https://cognito-idp.${config.cognito.region}.amazonaws.com/`;
  assertKnownOrigin(endpoint);
  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new CognitoError('NetworkError', 'Could not reach Birbly. Check the connection and try again.');
  }
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const type = String(payload.__type ?? payload.code ?? 'Error');
    const code = type.split('#').pop() ?? type;
    const message = String(payload.message ?? payload.Message ?? '');
    throw Object.assign(new CognitoError(code, friendly(code, message)), { status: response.status });
  }
  return payload as T;
}

interface ChallengeReply {
  ChallengeName?: string;
  ChallengeParameters?: Record<string, string>;
  Session?: string;
  AuthenticationResult?: Tokens;
}

function next(reply: ChallengeReply, username: string): SignInStep {
  if (reply.AuthenticationResult) return { kind: 'done', tokens: reply.AuthenticationResult };
  const name = reply.ChallengeName ?? '';
  const session = reply.Session ?? '';
  const who = reply.ChallengeParameters?.USER_ID_FOR_SRP ?? username;
  if (name === 'SOFTWARE_TOKEN_MFA' || name === 'SMS_MFA' || name === 'EMAIL_OTP') {
    const destination = reply.ChallengeParameters?.CODE_DELIVERY_DESTINATION;
    return { kind: 'code', challenge: name, session, username: who, ...(destination ? { destination } : {}) };
  }
  if (name === 'NEW_PASSWORD_REQUIRED') return { kind: 'new-password', session, username: who };
  throw new CognitoError('UnsupportedChallenge',
    'This account needs a sign-in step the app does not offer yet. Please use the Birbly website.');
}

const client = () => config.cognito.clientId;
const clean = (email: string) => email.trim().toLowerCase();

/** Email and password, proved with SRP. */
export async function signInWithPassword(email: string, password: string): Promise<SignInStep> {
  const secret = ephemeral();
  const username = clean(email);
  const first = await call<ChallengeReply>('InitiateAuth', {
    AuthFlow: 'USER_SRP_AUTH',
    ClientId: client(),
    AuthParameters: { USERNAME: username, SRP_A: secret.A.toString(16) },
  });
  if (first.ChallengeName !== 'PASSWORD_VERIFIER' || !first.ChallengeParameters) return next(first, username);
  const p = first.ChallengeParameters;
  const responses = await passwordVerifier({
    userPoolId: config.cognito.userPoolId,
    password,
    ephemeral: secret,
    challenge: { SALT: p.SALT!, SRP_B: p.SRP_B!, SECRET_BLOCK: p.SECRET_BLOCK!, USER_ID_FOR_SRP: p.USER_ID_FOR_SRP! },
  });
  const second = await call<ChallengeReply>('RespondToAuthChallenge', {
    ChallengeName: 'PASSWORD_VERIFIER',
    ClientId: client(),
    ChallengeResponses: responses,
    ...(first.Session ? { Session: first.Session } : {}),
  });
  return next(second, username);
}

const CODE_KEY: Record<CodeChallenge, string> = {
  SOFTWARE_TOKEN_MFA: 'SOFTWARE_TOKEN_MFA_CODE',
  SMS_MFA: 'SMS_MFA_CODE',
  EMAIL_OTP: 'EMAIL_OTP_CODE',
};

export async function answerCode(step: Extract<SignInStep, { kind: 'code' }>, code: string): Promise<SignInStep> {
  const reply = await call<ChallengeReply>('RespondToAuthChallenge', {
    ChallengeName: step.challenge,
    ClientId: client(),
    Session: step.session,
    ChallengeResponses: { USERNAME: step.username, [CODE_KEY[step.challenge]]: code.trim() },
  });
  return next(reply, step.username);
}

export async function setNewPassword(step: Extract<SignInStep, { kind: 'new-password' }>, password: string): Promise<SignInStep> {
  const reply = await call<ChallengeReply>('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: client(),
    Session: step.session,
    ChallengeResponses: { USERNAME: step.username, NEW_PASSWORD: password },
  });
  return next(reply, step.username);
}

/** Where Cognito sent a code, for the screen to name ("p***@example.com"). */
interface Delivery {
  CodeDeliveryDetails?: { Destination?: string };
}

export async function register(email: string, password: string): Promise<{ confirmed: boolean; destination?: string }> {
  const reply = await call<Delivery & { UserConfirmed?: boolean }>('SignUp', {
    ClientId: client(),
    Username: clean(email),
    Password: password,
    UserAttributes: [{ Name: 'email', Value: clean(email) }],
  });
  const destination = reply.CodeDeliveryDetails?.Destination;
  return { confirmed: Boolean(reply.UserConfirmed), ...(destination ? { destination } : {}) };
}

export async function confirmRegistration(email: string, code: string): Promise<void> {
  await call('ConfirmSignUp', { ClientId: client(), Username: clean(email), ConfirmationCode: code.trim() });
}

export async function resendConfirmation(email: string): Promise<void> {
  await call('ResendConfirmationCode', { ClientId: client(), Username: clean(email) });
}

export async function startReset(email: string): Promise<string | undefined> {
  const reply = await call<Delivery>('ForgotPassword', { ClientId: client(), Username: clean(email) });
  return reply.CodeDeliveryDetails?.Destination;
}

export async function finishReset(email: string, code: string, password: string): Promise<void> {
  await call('ConfirmForgotPassword', {
    ClientId: client(),
    Username: clean(email),
    ConfirmationCode: code.trim(),
    Password: password,
  });
}

/**
 * New tokens for a refresh token, through the API.
 *
 * With refresh token rotation on (Essentials, 20-api), this is the API's way
 * to refresh; auth/cognito.ts tries the token endpoint first, as it always
 * has, and comes here when that refuses.
 */
export async function refreshTokens(refreshToken: string): Promise<Tokens> {
  const reply = await call<{ AuthenticationResult?: Tokens }>('GetTokensFromRefreshToken', {
    ClientId: client(),
    RefreshToken: refreshToken,
  });
  if (!reply.AuthenticationResult) throw new CognitoError('NotAuthorizedException', 'Your sign-in has expired. Please sign in again.');
  return reply.AuthenticationResult;
}

/** Signing out: the refresh token stops working everywhere, no browser needed. */
export async function revoke(refreshToken: string): Promise<void> {
  await call('RevokeToken', { ClientId: client(), Token: refreshToken });
}
